import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { CalendarRange, Users, Building2, ScrollText, RefreshCw, Building, LogOut } from 'lucide-react';
import SchedulePage from '@/pages/SchedulePage';
import EmployeesPage from '@/pages/EmployeesPage';
import WorkplacesPage from '@/pages/WorkplacesPage';
import PravidlaPage from '@/pages/PravidlaPage';
import KlientiPage from '@/pages/KlientiPage';
import LoginPage from '@/pages/LoginPage';
import UvitaniePage from '@/pages/UvitaniePage';

const BASE_TABS = [
  { key: 'rozvrh', label: 'Rozvrh', icon: CalendarRange },
  { key: 'zamestnanci', label: 'Zamestnanci', icon: Users },
  { key: 'pracoviska', label: 'Pracoviská', icon: Building2 },
  { key: 'pravidla', label: 'Pravidlá', icon: ScrollText },
];

function AppContent() {
  const { user, lockedWorkplaceId, logout } = useAuth();
  const restricted = user?.role === 'prevadzka';
  const isDeveloper = user?.role === 'developer';
  const TABS = isDeveloper ? [{ key: 'klienti', label: 'Klienti', icon: Building }, ...BASE_TABS] : BASE_TABS;
  const [meta, setMeta] = useState({ roles: [] });
  const [workplaces, setWorkplaces] = useState([]);
  const [selectedId, setSelectedId] = useState(() => (restricted ? lockedWorkplaceId : localStorage.getItem('wp_selected')) || null);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState(isDeveloper ? 'klienti' : 'rozvrh');
  const [syncing, setSyncing] = useState(false);
  const [envLabel, setEnvLabel] = useState(null);

  const loadWorkplaces = useCallback(async () => {
    const res = await api.get('/workplaces');
    setWorkplaces(res.data);
    return res.data;
  }, []);

  const loadEmployees = useCallback(async (wid) => {
    if (!wid) { setEmployees([]); return; }
    const res = await api.get('/employees', { params: { workplace_id: wid } });
    setEmployees(res.data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const init = async (attempt = 1) => {
      try {
        try { const m = await api.get('/meta'); if (!cancelled) setMeta(m.data); } catch (e) { /* meta neblokuje */ }
        try { const c = await api.get('/sso/config'); if (!cancelled) setEnvLabel(c.data.env_label); } catch (e) { /* nekritické */ }
        let wps = await loadWorkplaces();
        if (!restricted && wps.length === 0) {
          await api.post('/seed');
          wps = await loadWorkplaces();
        }
        if (cancelled || !wps.length) return;
        if (restricted) {
          setSelectedId(lockedWorkplaceId || wps[0].id);
        } else {
          const stored = localStorage.getItem('wp_selected');
          const valid = stored && wps.some((w) => w.id === stored);
          setSelectedId(valid ? stored : wps[0].id);
        }
      } catch (e) {
        if (cancelled) return;
        if (attempt < 3) setTimeout(() => init(attempt + 1), 1200);
        else toast.error('Nepodarilo sa načítať pracoviská. Obnovte stránku.');
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadWorkplaces]);

  useEffect(() => {
    if (!restricted && selectedId) localStorage.setItem('wp_selected', selectedId);
  }, [selectedId, restricted]);

  useEffect(() => { loadEmployees(selectedId); }, [selectedId, loadEmployees]);

  const selected = workplaces.find((w) => w.id === selectedId);

  const syncCenter = async () => {
    if (!selected?.otido_center_id) { toast.error('Toto stredisko nemá väzbu na TiDo.'); return; }
    setSyncing(true);
    try {
      const now = new Date();
      const res = await api.post('/otido/import', {
        company_id: selected.company_id,
        center_id: selected.otido_center_id,
        rok: now.getFullYear(), mesiac: now.getMonth() + 1,
      });
      await loadWorkplaces();
      await loadEmployees(selectedId);
      toast.success(`Dáta synchronizované: ${res.data.name} (${res.data.employees_imported} zam., ${res.data.shifts} zmien)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Synchronizácia zlyhala');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="App min-h-screen bg-slate-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-heading font-bold">S</div>
            <div>
              <div className="font-heading font-bold text-slate-900 leading-tight flex items-center gap-2">
                Plánovač zmien
                {envLabel && (
                  <span data-testid="env-badge" className={`text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase ${envLabel === 'prod' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{envLabel}</span>
                )}
              </div>
              <div className="text-[11px] text-slate-400 leading-tight">Automatizovaný rozvrh · Zákonník práce SR</div>
            </div>
          </div>
          <nav className="flex items-center gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                >
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            {selected?.otido_center_id != null && (
              <Button data-testid="sync-center-btn" size="sm" variant="outline" onClick={syncCenter} disabled={syncing}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Synchronizujem…' : 'Synchronizovať'}
              </Button>
            )}
            {restricted ? (
              <div data-testid="locked-center-name" className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100 text-slate-700 text-sm font-medium">
                <Building className="w-4 h-4 text-slate-500" />
                {selected ? selected.name : 'Načítavam…'}
              </div>
            ) : (
              <Select value={selectedId || ''} onValueChange={setSelectedId}>
                <SelectTrigger data-testid="workplace-selector" className="w-56"><SelectValue placeholder="Pracovisko" /></SelectTrigger>
                <SelectContent>
                  {workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="logout-btn" size="sm" variant="ghost" onClick={logout}
              className="text-slate-500 hover:text-red-600" title={`Odhlásiť (${user?.username || ''})`}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-6">
        {tab === 'klienti' && isDeveloper && (
          <KlientiPage
            workplaces={workplaces}
            refreshWorkplaces={loadWorkplaces}
            onPlan={(wid) => { setSelectedId(wid); setTab('rozvrh'); }}
          />
        )}
        {tab === 'rozvrh' && <SchedulePage workplace={selected} employees={employees} />}
        {tab === 'zamestnanci' && <EmployeesPage workplace={selected} employees={employees} roles={meta.roles} refresh={() => loadEmployees(selectedId)} />}
        {tab === 'pracoviska' && <WorkplacesPage workplaces={workplaces} selectedId={selectedId} onSelect={setSelectedId} refresh={loadWorkplaces} meta={meta} embedded={restricted} />}
        {tab === 'pravidla' && <PravidlaPage />}
      </main>
    </div>
  );
}

function App() {
  const { status, error } = useAuth();

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center text-slate-500" data-testid="auth-loading">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin mb-4" />
        <div className="text-sm font-medium">Načítavam…</div>
      </div>
    );
  }
  if (status === 'denied') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div data-testid="auth-denied" className="max-w-lg p-6 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm text-center">
          {error || 'Prístup zamietnutý.'}
        </div>
      </div>
    );
  }
  if (status === 'anon') return <LoginPage />;
  return <AuthedApp />;
}

function AuthedApp() {
  const { needsUvitanie } = useAuth();
  if (needsUvitanie) return <UvitaniePage />;
  return <AppContent />;
}

export default App;
